import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useData } from "../../context/DataContext";
import { getMyCompensatoryPending } from "../../api/compensatory";
import {
  cancelOvertime,
  createEmployeeOvertime,
  getMyOvertimes,
} from "../../api/overtime";
import { useNavigation } from "../../context/NavigationContext";
import { buildResubmitDraft } from "../../utils/requestTransparency";
import RequestTransparency from "../common/RequestTransparency";
import { getActiveAttendanceRegulation } from "../../api/attendanceRegulations";
import {
  applyOvertimeHourMultiplier,
  compensatoryOvertimeRateHint,
  DEFAULT_OVERTIME_HOUR_RATES,
  paidOvertimeRateHint,
  ratesFromRegulation,
} from "../../utils/overtimeHourRates";
import "./OvertimeRequestForm.css";

const STATUS_LABELS = {
  PENDING: "قيد المراجعة",
  IN_PROGRESS: "قيد المراجعة",
  APPROVED: "معتمد",
  REJECTED: "مرفوض",
  CANCELLED: "ملغى",
  pending: "قيد المراجعة",
  in_progress: "قيد المراجعة",
  approved: "معتمد",
  rejected: "مرفوض",
  gm_pending: "قيد المراجعة",
};

const SETTLEMENT_LABELS = {
  PAID_OT: "صرف مالي",
  COMPENSATORY: "إذن تعويضي",
};

function formatDate(value) {
  if (!value) return "—";
  return String(value).slice(0, 10);
}

function OvertimeRequestForm() {
  const { refreshData } = useData();
  const navigation = useNavigation();
  const [pendingDays, setPendingDays] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [reason, setReason] = useState("");
  const [requestedSettlement, setRequestedSettlement] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [overtimeRates, setOvertimeRates] = useState(
    DEFAULT_OVERTIME_HOUR_RATES,
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [pending, mine, regulation] = await Promise.all([
        getMyCompensatoryPending(),
        getMyOvertimes(),
        getActiveAttendanceRegulation().catch(() => null),
      ]);
      setPendingDays(Array.isArray(pending) ? pending : pending?.items || []);
      setMyRequests(Array.isArray(mine) ? mine : mine?.items || []);
      setOvertimeRates(ratesFromRegulation(regulation));
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.message ||
        "تعذر تحميل بيانات الإضافي";
      setError(Array.isArray(message) ? message.join("\n") : message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const draft = navigation?.requestDraft;
    if (!draft || draft.kind !== "overtime") return undefined;
    setReason(draft.reason || "");
    setRequestedSettlement(draft.requestedSettlement || "");
    navigation.clearRequestDraft?.();
    return undefined;
  }, [navigation?.requestDraft]);

  const selectedHours = useMemo(() => {
    let total = 0;
    pendingDays.forEach((day) => {
      if (selectedIds.has(day.id)) {
        total += Number(day.overtimeHours || 0);
      }
    });
    return Math.round(total * 100) / 100;
  }, [pendingDays, selectedIds]);

  const paidPreviewHours = applyOvertimeHourMultiplier(
    selectedHours,
    overtimeRates.overtimePaidHourMultiplier,
  );
  const compensatoryPreviewHours = applyOvertimeHourMultiplier(
    selectedHours,
    overtimeRates.overtimeCompensatoryHourMultiplier,
  );

  function toggleDay(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    if (selectedIds.size === pendingDays.length) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(pendingDays.map((d) => d.id)));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (selectedIds.size === 0) {
      setError("اختر يوماً واحداً على الأقل من ساعات الزيادة");
      return;
    }

    if (reason.trim().length < 5) {
      setError("يجب كتابة سبب الإضافي (5 أحرف على الأقل)");
      return;
    }

    if (
      requestedSettlement !== "PAID_OT" &&
      requestedSettlement !== "COMPENSATORY"
    ) {
      setError("اختر رغبتك: إذن تعويضي أو صرف مالي");
      return;
    }

    setSubmitting(true);
    try {
      await createEmployeeOvertime({
        attendanceRecordIds: [...selectedIds],
        reason: reason.trim(),
        requestedSettlement,
      });
      setSuccess(
        "تم تقديم طلب الإضافي — يمر بموافقات السايكل ثم يقرر الموارد البشرية الصرف المالي أو الإذن التعويضي",
      );
      setSelectedIds(new Set());
      setReason("");
      setRequestedSettlement("");
      if (refreshData) await refreshData();
      await refresh();
    } catch (err) {
      const message =
        err.response?.data?.message || err.message || "تعذر تقديم الطلب";
      setError(Array.isArray(message) ? message.join("\n") : message);
    } finally {
      setSubmitting(false);
    }
  }

  function canCancelRequest(req) {
    const status = String(req?.status || "").toUpperCase();
    return status === "PENDING" || status === "IN_PROGRESS";
  }

  async function handleCancel(req) {
    if (!req?.id || !canCancelRequest(req)) return;
    if (
      !window.confirm(
        "هل تريد إلغاء طلب الإضافي؟ يمكنك تقديم طلب جديد بعد الإلغاء.",
      )
    ) {
      return;
    }

    setError("");
    setSuccess("");
    setCancellingId(req.id);
    try {
      await cancelOvertime(req.id);
      setSuccess("تم إلغاء طلب الإضافي بنجاح");
      if (refreshData) await refreshData();
      await refresh();
    } catch (err) {
      const message =
        err.response?.data?.message || err.message || "تعذر إلغاء الطلب";
      setError(Array.isArray(message) ? message.join("\n") : message);
    } finally {
      setCancellingId(null);
    }
  }

  return (
    <div className="ot-request" dir="rtl">
      <div className="ot-request-header">
        <h1 className="ot-request-title">طلب إضافي</h1>
        <p className="ot-request-subtitle">
          اختر أيام الزيادة، واكتب سبب الإضافي، وحدد رغبتك (إذن تعويضي أو صرف
          مالي). القرار النهائي للتسوية للموارد البشرية بعد سايكل الموافقات.
        </p>
        <div className="ot-request-rates" data-testid="ot-hour-rates">
          <strong>حسب لائحة الحضور</strong>
          <span>{paidOvertimeRateHint(overtimeRates.overtimePaidHourMultiplier)}</span>
          <span>
            {compensatoryOvertimeRateHint(
              overtimeRates.overtimeCompensatoryHourMultiplier,
            )}
          </span>
        </div>
      </div>

      {error && <p className="ot-request-error">{error}</p>}
      {success && <p className="ot-request-success">{success}</p>}

      <form className="ot-request-card" onSubmit={handleSubmit}>
        <div className="ot-request-card-head">
          <h2>ساعات الزيادة المعلقة (الشهر الحالي)</h2>
          {pendingDays.length > 0 && (
            <button
              type="button"
              className="ot-request-link-btn"
              onClick={toggleAll}
            >
              {selectedIds.size === pendingDays.length
                ? "إلغاء تحديد الكل"
                : "تحديد الكل"}
            </button>
          )}
        </div>

        {loading ? (
          <p className="ot-request-muted">جاري التحميل...</p>
        ) : pendingDays.length === 0 ? (
          <p className="ot-request-muted">
            لا توجد ساعات زيادة معلّقة هذا الشهر لتقديم طلب.
          </p>
        ) : (
          <ul className="ot-request-days">
            {pendingDays.map((day) => (
              <li key={day.id}>
                <label className="ot-request-day">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(day.id)}
                    onChange={() => toggleDay(day.id)}
                  />
                  <span className="ot-request-day-date">
                    {formatDate(day.date)}
                  </span>
                  <span className="ot-request-day-hours">
                    {Number(day.overtimeHours || 0).toFixed(2)} ساعة
                  </span>
                </label>
              </li>
            ))}
          </ul>
        )}

        <div className="ot-request-field">
          <label className="ot-request-label" htmlFor="ot-reason">
            سبب الإضافي *
          </label>
          <textarea
            id="ot-reason"
            className="ot-request-textarea"
            rows={3}
            required
            minLength={5}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="مثال: إغلاق وردية / تغطية نقص..."
          />
        </div>

        <fieldset className="ot-request-field ot-request-choice">
          <legend className="ot-request-label">رغبتك في التسوية *</legend>
          <p className="ot-request-hint">
            اختيارك استرشادي — الموارد البشرية تقرر الصرف المالي أو الإذن
            التعويضي.
          </p>
          <label className="ot-request-choice-option">
            <input
              type="radio"
              name="ot-requested-settlement"
              value="COMPENSATORY"
              checked={requestedSettlement === "COMPENSATORY"}
              onChange={() => setRequestedSettlement("COMPENSATORY")}
            />
            <span>
              إذونات تعويضي (رصيد ساعات)
              <small>
                {compensatoryOvertimeRateHint(
                  overtimeRates.overtimeCompensatoryHourMultiplier,
                )}
                {selectedHours > 0
                  ? ` — المحدد يُحسب ${compensatoryPreviewHours} ساعة رصيد`
                  : ""}
              </small>
            </span>
          </label>
          <label className="ot-request-choice-option">
            <input
              type="radio"
              name="ot-requested-settlement"
              value="PAID_OT"
              checked={requestedSettlement === "PAID_OT"}
              onChange={() => setRequestedSettlement("PAID_OT")}
            />
            <span>
              صرف مالي (أوفر تايم في المرتب)
              <small>
                {paidOvertimeRateHint(overtimeRates.overtimePaidHourMultiplier)}
                {selectedHours > 0
                  ? ` — المحدد يُحسب ${paidPreviewHours} ساعة في المرتب`
                  : ""}
              </small>
            </span>
          </label>
        </fieldset>

        <div className="ot-request-footer">
          <span className="ot-request-total">
            المجموع المحدد: <strong>{selectedHours}</strong> ساعة
          </span>
          <button
            type="submit"
            className="ot-request-submit"
            disabled={
              submitting ||
              selectedIds.size === 0 ||
              reason.trim().length < 5 ||
              !requestedSettlement
            }
          >
            {submitting ? "جاري الإرسال..." : "تقديم الطلب"}
          </button>
        </div>
      </form>

      <div className="ot-request-card ot-request-history">
        <h2>طلباتي السابقة</h2>
        {loading ? (
          <p className="ot-request-muted">جاري التحميل...</p>
        ) : myRequests.length === 0 ? (
          <p className="ot-request-muted">لا توجد طلبات إضافي بعد.</p>
        ) : (
          <ul className="ot-request-list">
            {myRequests.map((req) => (
              <li key={req.id} className="ot-request-item">
                <div className="ot-request-item-main">
                  <strong>{Number(req.hours || 0).toFixed(2)} ساعة</strong>
                  <span>
                    {formatDate(req.startDate)}
                    {req.endDate && req.endDate !== req.startDate
                      ? ` → ${formatDate(req.endDate)}`
                      : ""}
                  </span>
                </div>
                <div className="ot-request-item-meta">
                  <span className={`ot-status ot-status-${String(req.status || "").toLowerCase()}`}>
                    {STATUS_LABELS[req.status] || req.status || "—"}
                  </span>
                  {req.requestedSettlement && (
                    <span className="ot-settlement ot-preference">
                      رغبة:{" "}
                      {SETTLEMENT_LABELS[req.requestedSettlement] ||
                        req.requestedSettlement}
                    </span>
                  )}
                  {req.settlement && (
                    <span className="ot-settlement">
                      قرار HR:{" "}
                      {SETTLEMENT_LABELS[req.settlement] || req.settlement}
                    </span>
                  )}
                </div>
                {req.reason ? (
                  <p className="ot-request-item-reason">{req.reason}</p>
                ) : null}
                {canCancelRequest(req) && (
                  <div className="ot-request-item-actions">
                    <button
                      type="button"
                      className="ot-request-cancel-btn"
                      disabled={cancellingId === req.id}
                      onClick={() => handleCancel(req)}
                    >
                      {cancellingId === req.id
                        ? "جاري الإلغاء..."
                        : "إلغاء الطلب"}
                    </button>
                  </div>
                )}
                <RequestTransparency
                  request={{ ...req, _source: "overtime", type: "إضافي" }}
                  compact
                  onResubmit={(row) => {
                    const draft = buildResubmitDraft({
                      ...row,
                      _source: "overtime",
                      type: "إضافي",
                    });
                    if (!draft) return;
                    setReason(draft.reason || "");
                    setRequestedSettlement(draft.requestedSettlement || "");
                  }}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default OvertimeRequestForm;
